import {
  IGetInterviewByIdResponse,
  Meeting,
} from "@endpoints/types/interview.type";
import { ReviewFormData } from "@pages/CalendarsPage/SchedulingSection/form.types";
import {
  MeetingParties,
  separatePastAndNewProposals,
  TypeGroupOfProposals,
} from "@pages/MeetingDetailPage/MeetingDetailPage";
import { availabilitySlotsForApiPayload } from "@pages/CreateMeetingInvitePage/CreateMeetingInvitePage";
import { RootState } from "@store/reducers";
import { EnumUserTypes, userTypes } from "@utils/constants";
import { isObjectEmpty } from "@utils/helperFunction";
import dayjs from "@lib/dayjs";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSlotStatus } from "@pages/MeetingDetailPage/helpers/slotHelpers";

interface ReviewProposalSectionProps {
  interviewDetails: IGetInterviewByIdResponse;
  formData: ReviewFormData;
}

const ReviewProposalSection = (props: ReviewProposalSectionProps) => {
  const { interviewDetails, formData } = props;
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const groupOfProposals: TypeGroupOfProposals[] = useMemo(() => {
    return separatePastAndNewProposals(
      interviewDetails?.generalAvailabilitySlots
    );
  }, [interviewDetails?.generalAvailabilitySlots]);

  const isHiringCompanyInvolved = useMemo(
    () => !!interviewDetails?.hiringCompanyId,
    [interviewDetails?.hiringCompanyId]
  );
  const lastProposedBy = useMemo(
    () => groupOfProposals[0]?.slots[0]?.proposedBy,
    [groupOfProposals]
  );

  const isLatestProposalByMe = useMemo(
    () =>
      lastProposedBy?._id ===
      (user?.userType === userTypes.RECRUITER ? user?._id : user?.userId),
    [lastProposedBy, user]
  );

  useEffect(() => {
    if (isObjectEmpty(formData) || isObjectEmpty(interviewDetails)) {
      navigate(-1);
    }
  }, [formData, interviewDetails]);

  console.log("check", groupOfProposals, interviewDetails);

  if (isObjectEmpty(formData) || isObjectEmpty(interviewDetails)) {
    return null;
  }

  return (
    <div className="w-full flex flex-col flex-1 min-h-0">
      {/* Main body */}
      <div className="flex flex-col flex-1 min-h-0 overflow-y-scroll no-scrollbar">
        {/* Meeting details */}
        <div className="flex flex-col gap-3.5 mt-9">
          <div className="flex items-center">
            <span className="text-xs font-semibold">Organizer :&nbsp;</span>
            <span className="text-xs font-medium text-light-black">
              {interviewDetails?.createdBy?.firstName}{" "}
              {interviewDetails?.createdBy?.lastName}
            </span>
          </div>
          <div className="flex items-center">
            <span className="text-xs font-semibold">Meeting Title :&nbsp;</span>
            <span className="text-xs font-medium text-light-black">
              {interviewDetails?.meetingTitle}
            </span>
          </div>

          <div className="flex items-center">
            <span className="text-xs font-semibold">Meeting Type :&nbsp;</span>
            <span className="text-xs font-medium text-light-black">
              {interviewDetails?.meetingMode}
              {/* {MeetingModeEnum[interviewDetails?.meetingMode]} */}
            </span>
          </div>

          <div className="flex items-center">
            <span className="text-xs font-semibold">Meeting URL :&nbsp;</span>
            <span className="text-xs font-medium text-light-black">
              {interviewDetails?.url}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold">
              Meeting Description :&nbsp;
            </span>
            <span className="text-xs font-medium text-light-black">
              {interviewDetails?.meetingDescription}
            </span>
          </div>
        </div>

        {/* Availability Details */}
        <div className="flex justify-between border-y border-dashed mt-8 py-4 gap-10">
          <div className="flex flex-col w-1/2 gap-5">
            <span className="font-semibold text-xs text-charcoal">
              {lastProposedBy?.userType === userTypes.RECRUITER
                ? "Agent's"
                : lastProposedBy?.userType === userTypes.HIRING_COMPANY
                ? "Hiring company's"
                : "Candidate's"}{" "}
              proposed times
            </span>
            <div className="flex flex-col gap-4">
              {groupOfProposals[0]?.slots?.map((slot) => {
                const date = dayjs(slot?.startTime).format("dddd MMM Do");
                return (
                  <div
                    key={slot?._id}
                    className="grid grid-cols-2 items-center"
                  >
                    <span className="text-xs font-medium text-light-black">
                      {date}
                    </span>
                    <span className="text-xs font-medium text-light-black">
                      {dayjs(slot?.startTime).format("HH:mm")}
                      &nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
                      {dayjs(slot?.endTime).format("HH:mm")}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col w-1/2 gap-5">
            <span className="font-semibold text-xs text-charcoal">
              New proposed times
            </span>
            <div className="flex flex-col gap-4">
              {availabilitySlotsForApiPayload(formData?.availabilitySlots)?.map(
                (slot) => {
                  const date = dayjs(slot?.startTime).format("dddd MMM Do");
                  return (
                    <div className="grid grid-cols-2 items-center">
                      <span className="text-xs font-medium text-light-black">
                        {date}
                      </span>
                      <span className="text-xs font-medium text-light-black">
                        {dayjs(slot?.startTime).format("HH:mm")}-{" "}
                        {dayjs(slot?.endTime).format("HH:mm")}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>

        {/* Past Proposal Slots */}
        {groupOfProposals?.length > 1
          ? groupOfProposals?.map((proposal, proposalIndex) => {
              const proposedBy = proposal?.slots[0]?.proposedBy;
              const isSelfProposedSlots =
                proposedBy?._id ===
                (user?.userType === userTypes.RECRUITER ||
                user?.userType === userTypes.HIRING_COMPANY
                  ? user?._id
                  : user?.userId);
              const meetingResponder = () => {
                const parties = isHiringCompanyInvolved
                  ? MeetingParties
                  : [EnumUserTypes.CANDIDATE, EnumUserTypes.RECRUITER];
                return parties.filter(
                  (party) => proposedBy?.userType !== party
                );
              };

              return proposalIndex !== 0 || isLatestProposalByMe ? (
                <div
                  key={proposal?.createdAt}
                  className="mt-8 py-4 gap-10 bg-gray-50 rounded-md px-5"
                >
                  <div className="text-xs font-medium italic text-light-black mb-4">
                    Submitted at{" "}
                    {dayjs(proposal?.slots[0]?.createdAt).format(
                      "dddd Do MMM h:mm"
                    )}
                  </div>
                  <div className="flex justify-between">
                    {/* Column 1 */}
                    <div className="flex flex-col flex-1 gap-5">
                      <span className="font-semibold text-xs text-charcoal">
                        {isSelfProposedSlots
                          ? "Your"
                          : proposedBy?.userType === userTypes.CANDIDATE
                          ? "Candidate's"
                          : proposedBy?.userType === userTypes.HIRING_COMPANY
                          ? "Hiring company's"
                          : "Agent's"}{" "}
                        proposed times
                      </span>
                      <div className="flex flex-col gap-4">
                        {proposal?.slots?.map((slot) => {
                          const date = dayjs(slot?.startTime).format(
                            "dddd MMM Do"
                          );
                          return (
                            <div
                              key={slot?._id}
                              className="grid grid-cols-2 items-center"
                            >
                              <span className="text-xs font-medium text-light-black">
                                {date}
                              </span>
                              <span className="text-xs font-medium text-light-black">
                                {dayjs(slot?.startTime).format("HH:mm")}
                                &nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
                                {dayjs(slot?.endTime).format("HH:mm")}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Column 2 */}
                    {isHiringCompanyInvolved ? (
                      <div className="flex flex-col flex-1 gap-5 text-center">
                        <span className="font-semibold text-xs text-charcoal">
                          {meetingResponder()[1] === EnumUserTypes.CANDIDATE
                            ? "Candidate's"
                            : meetingResponder()[1] ===
                              EnumUserTypes.HIRING_COMPANY
                            ? "Hiring company's"
                            : "Agent's"}{" "}
                          response
                        </span>
                        <div className="flex flex-col gap-4">
                          {proposal?.slots?.map((slot) => {
                            return (
                              <span
                                key={slot?._id}
                                className="text-xs capitalize text-charcoal"
                              >
                                {getSlotStatus({
                                  slot,
                                  statusOf: meetingResponder()[1],
                                })}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {/* Column 3 */}
                    <div className="flex flex-col flex-1 gap-5 text-center">
                      <span className="font-semibold text-xs text-charcoal">
                        {meetingResponder()[0] === EnumUserTypes.RECRUITER
                          ? "Agent's"
                          : meetingResponder()[0] ===
                            EnumUserTypes.HIRING_COMPANY
                          ? "Hiring company's"
                          : "Candidate's"}{" "}
                        response
                      </span>
                      <div className="flex flex-col gap-4">
                        {proposal?.slots?.map((slot) => {
                          return (
                            <span
                              key={slot?._id}
                              className="text-xs capitalize text-charcoal"
                            >
                              {getSlotStatus({
                                slot,
                                statusOf: meetingResponder()[0],
                              })}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null;
            })
          : null}
      </div>
    </div>
  );
};

export default ReviewProposalSection;
